import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from '@/i18n'
import type { TFunction } from 'i18next'
import { apiService } from '@/service/api'
import { Search, Download, ChevronLeft, ChevronRight, Loader2, ChevronDown, Check } from 'lucide-react'
import { toast } from 'sonner'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import Paginator from '@/components/Paginator'

interface AuditLog {
  id: string
  event_type: string
  event_category: string
  resource_type: string
  resource_id: string | null
  resource_name: string | null
  actor_email: string | null
  actor_name: string | null
  ip_address: string | null
  action_description: string
  created_at: string
  metadata: Record<string, unknown>
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Stable action identifiers paired with their i18n label keys; resolved at render.
const ACTION_OPTIONS = [
  { value: 'document_upload', labelKey: 'settings:audit.actions.upload' },
  { value: 'document_update', labelKey: 'settings:audit.actions.edit' },
  { value: 'document_delete', labelKey: 'settings:audit.actions.delete' },
  { value: 'document_view', labelKey: 'settings:audit.actions.view' },
] as const

function mapAction(eventType: string, t: TFunction): string {
  if (eventType.includes('upload')) return t('settings:audit.actions.upload', { defaultValue: 'Upload' })
  if (eventType.includes('delete')) return t('settings:audit.actions.delete', { defaultValue: 'Delete' })
  if (eventType.includes('update') || eventType.includes('edit')) return t('settings:audit.actions.edit', { defaultValue: 'Edit' })
  if (eventType.includes('download')) return t('settings:audit.actions.download', { defaultValue: 'Download' })
  if (eventType.includes('view')) return t('settings:audit.actions.view', { defaultValue: 'View' })
  return eventType.replace('document_', '').replace(/_/g, ' ')
}

function getMonthDateRange(month: number, year: number) {
  const from = new Date(year, month, 1)
  const to = new Date(year, month + 1, 0, 23, 59, 59, 999)
  return { date_from: from.toISOString(), date_to: to.toISOString() }
}

function formatTimezoneOffset(): string {
  const offset = new Date().getTimezoneOffset()
  const sign = offset <= 0 ? '+' : '-'
  const hours = Math.abs(Math.floor(offset / 60))
  return `(${sign}${hours})`
}

/* ------------------------------------------------------------------ */
/*  Checkbox multi-select dropdown                                     */
/* ------------------------------------------------------------------ */
// null = all selected (default state). Set = specific items.
interface CheckboxDropdownProps {
  label: string
  // Noun shown in the "N <noun>" trigger label when a subset is selected.
  countNoun: string
  options: { value: string; label: string }[]
  selected: Set<string> | null
  onChange: (next: Set<string> | null) => void
}

function CheckboxDropdown({ label, countNoun, options, selected, onChange }: CheckboxDropdownProps) {
  const allSelected = selected === null
  const isChecked = (value: string) => allSelected || (selected !== null && selected.has(value))

  const toggleAll = () => {
    // If all selected -> deselect all. If not all -> select all.
    onChange(allSelected ? new Set<string>() : null)
  }

  const toggleItem = (value: string) => {
    if (allSelected) {
      // Was "all" -> uncheck this one item, so select everything except this one
      const next = new Set(options.map((o) => o.value))
      next.delete(value)
      onChange(next.size === 0 ? new Set<string>() : next)
    } else {
      const next = new Set(selected)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      // If all items re-checked, go back to null (all)
      if (next.size === options.length) {
        onChange(null)
      } else {
        onChange(next)
      }
    }
  }

  const triggerLabel = allSelected
    ? label
    : selected.size === 0
      ? label
      : `${selected.size} ${countNoun}`

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-between w-[200px] h-9 rounded-lg border border-input bg-background px-3 text-sm hover:bg-muted/50 transition-colors"
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[200px] p-1">
        {/* All toggle */}
        <button
          type="button"
          onClick={toggleAll}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors"
        >
          <span className={`flex size-4 shrink-0 items-center justify-center rounded border ${
            allSelected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input'
          }`}>
            {allSelected && <Check className="size-3" />}
          </span>
          {label}
        </button>

        {/* Individual items */}
        {options.map((opt) => {
          const checked = isChecked(opt.value)
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggleItem(opt.value)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors"
            >
              <span className={`flex size-4 shrink-0 items-center justify-center rounded border ${
                checked
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input'
              }`}>
                {checked && <Check className="size-3" />}
              </span>
              {opt.label}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
export default function AuditTrailV2() {
  const { t } = useTranslation()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<Set<string> | null>(null)
  const [selectedActions, setSelectedActions] = useState<Set<string> | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [total, setTotal] = useState(0)
  const [monthPickerOpen, setMonthPickerOpen] = useState(false)
  const [monthYear, setMonthYear] = useState(() => {
    const now = new Date()
    return { month: now.getMonth(), year: now.getFullYear() }
  })
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear())

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const { date_from, date_to } = getMonthDateRange(monthYear.month, monthYear.year)
      const params = new URLSearchParams({
        page_number: (page - 1).toString(),
        page_size: pageSize.toString(),
        date_from,
        date_to,
      })
      if (searchQuery) params.append('search', searchQuery)
      // API only supports single event_type, so if exactly one action selected send it
      if (selectedActions !== null && selectedActions.size === 1) {
        params.append('event_type', [...selectedActions][0])
      }
      // API only supports single actor_email
      if (selectedUsers !== null && selectedUsers.size === 1) {
        params.append('actor_email', [...selectedUsers][0])
      }
      const response = await apiService.fetchOrError(apiService.getUrl(`v1/audit/logs?${params.toString()}`))
      const data = await response.json()
      setLogs(data.logs || [])
      setTotal(data.total || 0)
    } catch {
      console.error('Failed to fetch audit logs')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, searchQuery, selectedActions, selectedUsers, monthYear])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  // Unique users for the filter
  const uniqueUsers = useMemo(() => {
    const names = new Map<string, string>()
    logs.forEach((l) => {
      if (l.actor_name && l.actor_email) names.set(l.actor_email, l.actor_name)
    })
    return Array.from(names.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([email, name]) => ({ value: email, label: name }))
  }, [logs])

  // Client-side filtering for multi-select (API only supports single value)
  const filteredLogs = useMemo(() => {
    let result = logs
    // null = all selected, no filtering needed
    if (selectedActions !== null) {
      if (selectedActions.size === 0) return []
      // When >1 action selected, filter client-side (API only handles single)
      if (selectedActions.size > 1) {
        result = result.filter((l) => selectedActions.has(l.event_type))
      }
      // size === 1 is already handled server-side
    }
    if (selectedUsers !== null) {
      if (selectedUsers.size === 0) return []
      if (selectedUsers.size > 1) {
        result = result.filter((l) => l.actor_email && selectedUsers.has(l.actor_email))
      }
    }
    return result
  }, [logs, selectedActions, selectedUsers])

  const resetPage = () => setPage(1)

  const prevMonth = () => {
    setMonthYear((prev) => {
      if (prev.month === 0) return { month: 11, year: prev.year - 1 }
      return { month: prev.month - 1, year: prev.year }
    })
    resetPage()
  }

  const nextMonth = () => {
    const now = new Date()
    const current = monthYear.year * 12 + monthYear.month
    const max = now.getFullYear() * 12 + now.getMonth()
    if (current >= max) return
    setMonthYear((prev) => {
      if (prev.month === 11) return { month: 0, year: prev.year + 1 }
      return { month: prev.month + 1, year: prev.year }
    })
    resetPage()
  }

  const selectMonth = (month: number) => {
    const now = new Date()
    if (pickerYear > now.getFullYear() || (pickerYear === now.getFullYear() && month > now.getMonth())) return
    setMonthYear({ month, year: pickerYear })
    setMonthPickerOpen(false)
    resetPage()
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const { date_from, date_to } = getMonthDateRange(monthYear.month, monthYear.year)
      const params = new URLSearchParams({ date_from, date_to })
      if (searchQuery) params.append('search', searchQuery)
      if (selectedActions !== null && selectedActions.size === 1) params.append('event_type', [...selectedActions][0])
      if (selectedUsers !== null && selectedUsers.size === 1) params.append('actor_email', [...selectedUsers][0])
      const response = await apiService.fetchOrError(apiService.getUrl(`v1/audit/logs/export?${params.toString()}`))
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `audit-logs-${MONTHS[monthYear.month]}-${monthYear.year}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t('settings:audit.export.failed', { defaultValue: 'Failed to export audit logs' }))
    } finally {
      setExporting(false)
    }
  }

  const handleUserFilterChange = (next: Set<string> | null) => {
    setSelectedUsers(next)
    resetPage()
  }

  const handleActionFilterChange = (next: Set<string> | null) => {
    setSelectedActions(next)
    resetPage()
  }

  const tzLabel = formatTimezoneOffset()
  const now = new Date()
  const totalPages = Math.ceil(total / pageSize)

  const actionOptions = ACTION_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="relative w-[200px]">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <IntuiInput
              placeholder={t('settings:audit.searchPlaceholder', { defaultValue: 'Search' })}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); resetPage() }}
              className="h-9 ps-9"
            />
          </div>
          <CheckboxDropdown
            label={t('settings:audit.filters.allUsers', { defaultValue: 'All Users' })}
            countNoun={t('settings:audit.filters.usersNoun', { defaultValue: 'Users' })}
            options={uniqueUsers}
            selected={selectedUsers}
            onChange={handleUserFilterChange}
          />
          <CheckboxDropdown
            label={t('settings:audit.filters.allActions', { defaultValue: 'All Actions' })}
            countNoun={t('settings:audit.filters.actionsNoun', { defaultValue: 'Actions' })}
            options={actionOptions}
            selected={selectedActions}
            onChange={handleActionFilterChange}
          />
        </div>
        <div className="flex items-center gap-2">
          <IntuiButton
            variant="outline"
            size="icon"
            className="size-9"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
          </IntuiButton>
          <div className="flex items-center">
            <IntuiButton
              variant="outline"
              size="icon"
              className="size-9"
              onClick={prevMonth}
            >
              <ChevronLeft className="size-4" />
            </IntuiButton>
            <Popover open={monthPickerOpen} onOpenChange={setMonthPickerOpen}>
              <PopoverTrigger asChild>
                <IntuiButton
                  variant="outline"
                  onClick={() => { setPickerYear(monthYear.year); setMonthPickerOpen(true) }}
                  className="text-sm font-medium min-w-[90px] h-9 px-3"
                >
                  {MONTHS[monthYear.month]} {monthYear.year}
                </IntuiButton>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[270px] p-3">
                {/* Year nav */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={() => setPickerYear((y) => y - 1)}
                    className="size-7 inline-flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                  >
                    <ChevronLeft className="size-4" />
                  </button>
                  <span className="text-sm font-medium">{pickerYear}</span>
                  <button
                    type="button"
                    onClick={() => { if (pickerYear < now.getFullYear()) setPickerYear((y) => y + 1) }}
                    disabled={pickerYear >= now.getFullYear()}
                    className="size-7 inline-flex items-center justify-center rounded-md hover:bg-muted transition-colors disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <ChevronRight className="size-4" />
                  </button>
                </div>
                {/* Month grid */}
                <div className="grid grid-cols-4 gap-1">
                  {MONTHS.map((m, i) => {
                    const isFuture = pickerYear > now.getFullYear() || (pickerYear === now.getFullYear() && i > now.getMonth())
                    const isSelected = i === monthYear.month && pickerYear === monthYear.year
                    return (
                      <button
                        key={m}
                        type="button"
                        disabled={isFuture}
                        onClick={() => selectMonth(i)}
                        className={`h-8 rounded-full text-sm transition-colors ${
                          isSelected
                            ? 'bg-foreground text-background font-medium'
                            : isFuture
                              ? 'text-muted-foreground/40 cursor-not-allowed'
                              : 'hover:bg-muted text-foreground'
                        }`}
                      >
                        {m}
                      </button>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>
            <IntuiButton
              variant="outline"
              size="icon"
              className="size-9"
              onClick={nextMonth}
              disabled={monthYear.year === now.getFullYear() && monthYear.month === now.getMonth()}
            >
              <ChevronRight className="size-4" />
            </IntuiButton>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="w-full">
            {/* Header */}
            <div className="flex items-center h-[52px] border-b border-[#e5e5e5] dark:border-dark-border">
              <span className="w-[144px] shrink-0 px-2 text-sm font-medium text-muted-foreground">{t('settings:audit.tableHeaders.user', { defaultValue: 'User' })}</span>
              <span className="flex-1 min-w-0 px-2 text-sm font-medium text-muted-foreground">{t('settings:audit.tableHeaders.document', { defaultValue: 'Document' })}</span>
              <span className="w-[88px] shrink-0 px-2 text-sm font-medium text-muted-foreground">{t('settings:audit.tableHeaders.action', { defaultValue: 'Action' })}</span>
              <span className="w-[172px] shrink-0 px-2 text-sm font-medium text-muted-foreground">{t('settings:audit.tableHeaders.dateTime', { defaultValue: 'Date & Time' })}</span>
              <span className="w-[197px] shrink-0 px-2 text-sm font-medium text-muted-foreground">{t('settings:audit.tableHeaders.ipAddress', { defaultValue: 'IP Address' })}</span>
            </div>

            {/* Rows */}
            {filteredLogs.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                {t('settings:audit.noLogsForMonth', { month: MONTHS[monthYear.month], year: monthYear.year, defaultValue: 'No audit logs for {{month}} {{year}}' })}
              </div>
            ) : (
              filteredLogs.map((log) => {
                const country = (log.metadata?.country as string) || ''
                const ipDisplay = log.ip_address
                  ? country ? `${log.ip_address} (${country})` : log.ip_address
                  : '\u2014'
                return (
                  <div
                    key={log.id}
                    className="flex items-center h-[52px] border-b border-[#e5e5e5] dark:border-dark-border hover:bg-muted/50 transition-colors"
                  >
                    <span className="w-[144px] shrink-0 px-2 text-sm font-medium truncate">
                      {log.actor_name || t('settings:audit.unknownUser', { defaultValue: 'Unknown' })}
                    </span>
                    <span className="flex-1 min-w-0 px-2 text-sm text-muted-foreground truncate">
                      {log.resource_name || log.action_description}
                    </span>
                    <span className="w-[88px] shrink-0 px-2 text-sm text-muted-foreground">
                      {mapAction(log.event_type, t)}
                    </span>
                    <span className="w-[172px] shrink-0 px-2 text-sm text-muted-foreground">
                      {new Date(log.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}{' '}
                      {new Date(log.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}{' '}
                      {tzLabel}
                    </span>
                    <span className="w-[197px] shrink-0 px-2 text-sm text-muted-foreground truncate">
                      {ipDisplay}
                    </span>
                  </div>
                )
              })
            )}

            {/* Pagination */}
            {!loading && total > 0 && (
              <Paginator
                currentPage={page}
                totalPages={totalPages}
                totalItems={total}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
